import { useEffect, useState, useRef, useCallback } from "react";
import {
  Alignment,
  Fit,
  Layout,
  useRive,
  useStateMachineInput
} from "@rive-app/react-canvas";
import { EventType } from "@rive-app/canvas";
import { ChatMessage } from "@/entities/messaging/types";
import { isEmpty } from "lodash";
import { stripMarkdownLinks } from "@/utils/strings";

export enum DreamerState {
  Collapsed = 0,
  Normal = 1,
  Notification = 5
}

const MAX_NOTIFICATION_LENGTH = 30; // total chars including ellipsis, sized to fit Rive artboard text run
const ANIMATION_DURATION_MS = 600;
const TRANSITION_DELAY_MS = 500;
const MESSAGE_REVEAL_START_DELAY_MS = ANIMATION_DURATION_MS + 100;
const BADGE_TO_MESSAGE_REVEAL_DELAY_MS = 180;
const MESSAGE_REVEAL_DURATION_MS = 180;
const MESSAGE_REVEAL_PREVIEW_LENGTH = 3;

interface Props {
  title: string;
  state: DreamerState;
  animatedHighlight: boolean;
  notifications?: ChatMessage[];
  notifyTimeout?: number;
  notificationCount: number;
}

const DreamerButton: React.FC<Props> = ({
  title,
  state,
  animatedHighlight,
  notifications = [],
  notifyTimeout = 5000,
  notificationCount
}: Props) => {
  const STATE_MACHINE_NAME = "State Machine 1";
  const inputNames = [
    "is_GradientStop",
    "is_Message",
    "is_Circle-1",
    "is_Circle-2",
    "is_Circle-3",
    "icon_Stars",
    "icon_Bell",
    "icon_Warning"
  ];

  const [internalState, setInternalState] = useState<DreamerState>(state);
  const [notificationsQueue, setNotificationsQueue] = useState<ChatMessage[]>(
    []
  );

  const queueRef = useRef<ChatMessage[]>([]);
  const generationRef = useRef(0);
  const isTransitioning = useRef(false);
  const isShowingNotification = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<Set<number>>(new Set());
  const isMountedRef = useRef(true);
  const pendingCancelRef = useRef(false);
  const pendingNotificationTitleRef = useRef<string | null>(null);

  // Refs for stable access in timeout callbacks (avoids stale closures)
  const riveRef = useRef<ReturnType<typeof useRive>["rive"]>(null);
  const machineInputsRef = useRef<
    Record<string, ReturnType<typeof useStateMachineInput>>
  >({});
  const titleRef = useRef(title);
  titleRef.current = title;
  const notificationCountRef = useRef(notificationCount);
  notificationCountRef.current = notificationCount;
  const notificationObjectIdsRef = useRef<WeakMap<object, number>>(
    new WeakMap()
  );
  const nextNotificationObjectIdRef = useRef(1);
  const seenNotificationKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    queueRef.current = notificationsQueue;
  }, [notificationsQueue]);

  const safeSetTimeout = useCallback(
    (callback: () => void, delay: number): number => {
      const timeoutId = window.setTimeout(() => {
        timeoutsRef.current.delete(timeoutId);
        if (isMountedRef.current) {
          callback();
        }
      }, delay);
      timeoutsRef.current.add(timeoutId);
      return timeoutId;
    },
    []
  );

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach((id) => {
      window.clearTimeout(id);
    });
    timeoutsRef.current.clear();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  const dreamerButtonUrl = new URL(
    "@/assets/dreamer-button-v3-2.riv",
    import.meta.url
  ).href;
  const { rive, RiveComponent } = useRive({
    src: dreamerButtonUrl,
    artboard: "Dreamer-Button",
    autoplay: true,
    stateMachines: STATE_MACHINE_NAME,
    onLoadError: () => {},
    onLoad: () => {},
    layout: new Layout({
      fit: Fit.FitHeight,
      alignment: Alignment.Center
    })
  });

  riveRef.current = rive;

  useEffect(() => {
    return () => {
      if (rive) {
        rive.cleanup();
      }
    };
  }, [rive]);

  // useStateMachineInput subscribes to EventType.Load after it has already fired,
  // so its one synchronous stateMachineInputs() call sometimes races the WASM
  // state machine commit and permanently returns null. This effect bypasses that
  // by querying inputs directly and falling back to EventType.Play.
  useEffect(() => {
    if (!rive) return;

    let initialized = false;

    const initInputs = () => {
      if (initialized) return;
      const inputs = rive.stateMachineInputs(STATE_MACHINE_NAME);
      if (!inputs?.length) return;

      initialized = true;
      machineInputsRef.current = Object.fromEntries(
        inputs.map((input) => [input.name, input])
      );

      const iconStars = machineInputsRef.current["icon_Stars"];
      const isGradientStop = machineInputsRef.current["is_GradientStop"];

      if (isGradientStop) isGradientStop.value = !animatedHighlight;

      if (iconStars && !pendingCancelRef.current && !isShowingNotification.current) {
        iconStars.value = true;
        rive.setTextRunValue("Title", titleRef.current);
      }
    };

    initInputs();
    rive.on(EventType.Play, initInputs);
    return () => rive.off(EventType.Play, initInputs);
  }, [rive]); // animatedHighlight intentionally omitted — one-shot init only

  const machineInputHooks = Object.fromEntries(
    inputNames.map((name) => [
      name,
      useStateMachineInput(rive, STATE_MACHINE_NAME, name)
    ])
  );
  if (Object.values(machineInputHooks).every(Boolean)) {
    machineInputsRef.current = machineInputHooks;
  }

  const badgeDisplayedCountRef = useRef(0);
  const badgeVisibleRef = useRef(false);
  const pendingBadgeStepsRef = useRef(0);
  const badgeTransitioningRef = useRef(false);

  const getCircleLevelForCount = useCallback((count: number): number => {
    const normalized = Math.max(0, Math.trunc(count));
    if (normalized === 0) return 0;
    if (normalized <= 9) return 1;
    if (normalized <= 99) return 2;
    return 3;
  }, []);

  const setBadgeDisplayNumber = useCallback((number: number) => {
    try {
      const currentRive = riveRef.current;
      if (currentRive) {
        const normalized = Math.max(0, Math.min(999, Math.trunc(number)));
        const badgeText = normalized === 0 ? "" : String(normalized);
        ["Circle1Text", "Circle2Text", "Circle3Text"].forEach((textRun) => {
          currentRive.setTextRunValue(textRun, badgeText);
        });
      }
    } catch {
      // Ignore files without this text run.
    }
  }, []);

  const setBadgeLevelImmediate = useCallback(
    (count: number) => {
      const normalizedCount = Math.max(0, Math.trunc(count));
      const activeLevel = getCircleLevelForCount(normalizedCount);

      const applyCircleValue = (
        input: ReturnType<typeof useStateMachineInput> | undefined,
        enabled: boolean
      ) => {
        if (!input) return;
        if (typeof input.value === "number") {
          input.value = enabled ? 1 : 0;
        } else {
          input.value = enabled;
        }
      };

      const inputs = machineInputsRef.current;
      const isCircle1 = inputs["is_Circle-1"];
      const isCircle2 = inputs["is_Circle-2"];
      const isCircle3 = inputs["is_Circle-3"];

      applyCircleValue(isCircle1, activeLevel === 1);
      applyCircleValue(isCircle2, activeLevel === 2);
      applyCircleValue(isCircle3, activeLevel === 3);
      badgeVisibleRef.current = activeLevel > 0;
    },
    [getCircleLevelForCount]
  );

  const showBadgeImmediate = useCallback(
    (count: number) => {
      const normalizedCount = Math.max(0, Math.min(999, Math.trunc(count)));
      setBadgeDisplayNumber(normalizedCount);
      badgeDisplayedCountRef.current = normalizedCount;
      setBadgeLevelImmediate(normalizedCount);
    },
    [setBadgeDisplayNumber, setBadgeLevelImmediate]
  );

  const hideBadgeImmediate = useCallback(() => {
    setBadgeLevelImmediate(0);
  }, [setBadgeLevelImmediate]);

  const requestBadgeSteps = useCallback((stepCount: number) => {
    const safeSteps = Math.max(0, Math.trunc(stepCount));
    if (safeSteps === 0) return;
    pendingBadgeStepsRef.current += safeSteps;
  }, []);

  const getNotificationKey = useCallback((notification: ChatMessage): string => {
    const candidate = notification as unknown as Record<string, unknown>;
    const explicitId =
      candidate["id"] ??
      candidate["messageId"] ??
      candidate["uuid"] ??
      candidate["notificationId"];

    if (explicitId !== undefined && explicitId !== null && explicitId !== "") {
      return `id:${String(explicitId)}`;
    }

    const timestamp =
      candidate["createdAt"] ??
      candidate["timestamp"] ??
      candidate["sentAt"] ??
      candidate["receivedAt"];
    const message = candidate["message"];
    const sender =
      candidate["senderId"] ?? candidate["authorId"] ?? candidate["from"];

    if (timestamp !== undefined && timestamp !== null && timestamp !== "") {
      return `msg:${String(message ?? "")}|sender:${String(
        sender ?? ""
      )}|ts:${String(timestamp ?? "")}`;
    }

    if (notification && typeof notification === "object") {
      const asObject = notification as unknown as object;
      const existing = notificationObjectIdsRef.current.get(asObject);
      if (existing !== undefined) {
        return `obj:${existing}`;
      }

      const newId = nextNotificationObjectIdRef.current++;
      notificationObjectIdsRef.current.set(asObject, newId);
      return `obj:${newId}`;
    }

    return `raw:${String(notification)}`;
  }, []);

  const transitionBadgeForNextMessage = useCallback(
    (generation: number, onDone: () => void) => {
      if (generationRef.current !== generation) return;

      if (pendingBadgeStepsRef.current <= 0) {
        onDone();
        return;
      }

      const currentCount = badgeDisplayedCountRef.current;
      const nextCount = Math.min(999, currentCount + 1);
      pendingBadgeStepsRef.current = Math.max(
        0,
        pendingBadgeStepsRef.current - 1
      );
      showBadgeImmediate(nextCount);
      onDone();
    },
    [showBadgeImmediate]
  );

  // Cancel all pending notification work and let the Rive state machine
  // animate back to idle. Only set is_Message=false — the state machine handles
  // the full dismiss chain (Message-Disappears → Warning-To-Stars → Idle).
  // Manually setting icon_Warning/icon_Stars would short-circuit those transitions.
  const cancelNotificationCycle = useCallback(() => {
    generationRef.current++;
    clearAllTimeouts();
    isTransitioning.current = false;
    isShowingNotification.current = false;
    pendingNotificationTitleRef.current = null;
    pendingBadgeStepsRef.current = 0;
    badgeTransitioningRef.current = false;
    setNotificationsQueue([]);
    queueRef.current = [];
    pendingCancelRef.current = true;

    const inputs = machineInputsRef.current;
    if (inputs["is_Message"]) inputs["is_Message"].value = false;

    safeSetTimeout(() => {
      if (riveRef.current) {
        riveRef.current.setTextRunValue("Title", titleRef.current);
      }
      pendingCancelRef.current = false;

      if (
        queueRef.current.length > 0 &&
        notificationCountRef.current > 0
      ) {
        showNextNotificationRef.current();
      }
    }, ANIMATION_DURATION_MS * 2);
  }, [clearAllTimeouts, safeSetTimeout]);

  const cancelNotificationCycleRef = useRef(cancelNotificationCycle);
  cancelNotificationCycleRef.current = cancelNotificationCycle;
  const showNextNotificationRef = useRef<() => void>(() => {});

  const revealNotificationTitle = useCallback(
    (generation: number, fullTitle: string) => {
      const latestRive = riveRef.current;
      if (!latestRive) return;

      const totalLength = fullTitle.length;
      if (totalLength === 0) {
        latestRive.setTextRunValue("Title", fullTitle);
        return;
      }

      const previewLength = Math.min(totalLength, MESSAGE_REVEAL_PREVIEW_LENGTH);
      const remainingLength = totalLength - previewLength;
      const steps = remainingLength > 0 ? Math.min(remainingLength, 4) : 1;
      const charsPerStep =
        remainingLength > 0 ? Math.ceil(remainingLength / steps) : totalLength;
      const stepDelay = Math.max(
        10,
        Math.floor(MESSAGE_REVEAL_DURATION_MS / steps)
      );

      latestRive.setTextRunValue("Title", "");

      const revealStep = (stepIndex: number) => {
        if (generationRef.current !== generation) return;
        if (!isShowingNotification.current) return;
        if (pendingNotificationTitleRef.current !== fullTitle) return;

        const currentRive = riveRef.current;
        if (!currentRive) return;

        const revealedLength =
          stepIndex === 1
            ? previewLength
            : Math.min(
                totalLength,
                previewLength + (stepIndex - 1) * charsPerStep
              );
        currentRive.setTextRunValue("Title", fullTitle.slice(0, revealedLength));

        if (revealedLength >= totalLength) return;

        safeSetTimeout(() => {
          revealStep(stepIndex + 1);
        }, stepDelay);
      };

      revealStep(1);
    },
    [safeSetTimeout]
  );

  useEffect(() => {
    const captureEvents = () => {
      if (isShowingNotification.current) {
        cancelNotificationCycleRef.current();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("click", captureEvents, true);
      container.addEventListener("pointerdown", captureEvents, true);
    }

    return () => {
      if (container) {
        container.removeEventListener("click", captureEvents, true);
        container.removeEventListener("pointerdown", captureEvents, true);
      }
    };
  }, []);

  useEffect(() => {
    if (notifications.length === 0) return;

    const existingKeys = new Set([
      ...seenNotificationKeysRef.current,
      ...queueRef.current.map(getNotificationKey)
    ]);
    const uniqueIncoming = notifications.filter((notification) => {
      const key = getNotificationKey(notification);
      if (existingKeys.has(key)) return false;
      existingKeys.add(key);
      seenNotificationKeysRef.current.add(key);
      return true;
    });

    if (uniqueIncoming.length === 0) return;

    setNotificationsQueue((prev) => [...prev, ...uniqueIncoming]);
    requestBadgeSteps(uniqueIncoming.length);
  }, [notifications, requestBadgeSteps, getNotificationKey]);

  useEffect(() => {
    if (!rive) return;

    if (notificationCount === 0) {
      pendingBadgeStepsRef.current = 0;
      badgeTransitioningRef.current = false;
      seenNotificationKeysRef.current.clear();
      badgeDisplayedCountRef.current = 0;
      setBadgeDisplayNumber(0);
      hideBadgeImmediate();

      if (isShowingNotification.current) {
        cancelNotificationCycle();
      } else if (!pendingCancelRef.current) {
        generationRef.current++;
        clearAllTimeouts();
        isTransitioning.current = false;
        badgeTransitioningRef.current = false;
      }
      setNotificationsQueue([]);
      queueRef.current = [];
      setInternalState(DreamerState.Normal);
    }
  }, [
    rive,
    notificationCount,
    cancelNotificationCycle,
    clearAllTimeouts,
    setBadgeDisplayNumber,
    hideBadgeImmediate
  ]);

  const truncateNotificationIfNeeded = (
    str: string,
    maxLength: number = MAX_NOTIFICATION_LENGTH
  ): string => {
    const plain = stripMarkdownLinks(str);
    return plain.length > maxLength ? plain.slice(0, maxLength - 3) + "..." : plain;
  };

  const transitionToState = useCallback(
    (newState: DreamerState) => {
      if (isTransitioning.current) return;

      isTransitioning.current = true;
      setInternalState(newState);

      safeSetTimeout(() => {
        isTransitioning.current = false;
      }, TRANSITION_DELAY_MS);
    },
    [safeSetTimeout]
  );

  const showNextNotification = useCallback(() => {
    if (isShowingNotification.current) return;
    if (notificationCountRef.current === 0) return;

    const currentRive = riveRef.current;
    if (!currentRive) return;
    if (queueRef.current.length === 0) return;

    const generation = generationRef.current;
    const hadVisibleBadge = badgeVisibleRef.current;
    isShowingNotification.current = true;
    hideBadgeImmediate();

    transitionToState(DreamerState.Normal);

    safeSetTimeout(() => {
      if (generationRef.current !== generation) return;

      const message = queueRef.current[0]?.message;
      if (!message) {
        isShowingNotification.current = false;
        return;
      }

      const inputs = machineInputsRef.current;
      const isMessageHook = inputs["is_Message"];
      const iconWarningHook = inputs["icon_Warning"];

      if (!isMessageHook || !iconWarningHook) {
        isShowingNotification.current = false;
        return;
      }

      pendingNotificationTitleRef.current = truncateNotificationIfNeeded(message);
      currentRive.setTextRunValue("Title", "");
      iconWarningHook.value = true;
      isMessageHook.value = true;
      safeSetTimeout(() => {
        if (generationRef.current !== generation) return;

        const nextTitle = pendingNotificationTitleRef.current;
        if (!nextTitle) return;

        revealNotificationTitle(generation, nextTitle);
      }, MESSAGE_REVEAL_START_DELAY_MS + (hadVisibleBadge ? BADGE_TO_MESSAGE_REVEAL_DELAY_MS : 0));

      safeSetTimeout(() => {
        if (generationRef.current !== generation) return;

        isMessageHook.value = false;

        safeSetTimeout(() => {
          if (generationRef.current !== generation) return;

          const latestRive = riveRef.current;
          if (latestRive) {
            latestRive.setTextRunValue("Title", titleRef.current);
          }
          pendingNotificationTitleRef.current = null;

          transitionBadgeForNextMessage(generation, () => {
            if (generationRef.current !== generation) return;

            isShowingNotification.current = false;

            setNotificationsQueue((prevQueue) => {
              return prevQueue.slice(1);
            });
          });
        }, ANIMATION_DURATION_MS);
      }, notifyTimeout);
    }, ANIMATION_DURATION_MS);
  }, [
    hideBadgeImmediate,
    notifyTimeout,
    revealNotificationTitle,
    safeSetTimeout,
    transitionToState,
    transitionBadgeForNextMessage
  ]);
  showNextNotificationRef.current = showNextNotification;

  useEffect(() => {
    if (!rive || !machineInputHooks || isEmpty(machineInputHooks)) return;

    const isMessageHook = machineInputHooks["is_Message"];
    const iconWarningHook = machineInputHooks["icon_Warning"];
    const iconStarsHook = machineInputHooks["icon_Stars"];
    const isSmallCircle = machineInputHooks["is_Circle-1"];
    const isGradientStop = machineInputHooks["is_GradientStop"];

    if (isGradientStop) {
      isGradientStop.value = !animatedHighlight;
    }

    if (!isMessageHook || !iconWarningHook || !isSmallCircle || !iconStarsHook)
      return;

    switch (internalState) {
      case DreamerState.Collapsed:
      case DreamerState.Normal: {
        if (!pendingCancelRef.current && !isShowingNotification.current) {
          iconStarsHook.value = true;
          rive.setTextRunValue("Title", title);
          isMessageHook.value = false;
          iconWarningHook.value = false;

          if (notificationCountRef.current === 0) {
            hideBadgeImmediate();
          }
        }
        break;
      }

      case DreamerState.Notification: {
        if (!isShowingNotification.current) {
          showNextNotification();
        }
        break;
      }
    }
  }, [
    rive,
    internalState,
    machineInputHooks,
    animatedHighlight,
    title,
    showNextNotification,
    cancelNotificationCycle,
    hideBadgeImmediate
  ]);

  useEffect(() => {
    if (notificationsQueue.length === 0) return;
    if (notificationCountRef.current === 0) return;
    if (pendingCancelRef.current || isShowingNotification.current) return;

    const generation = generationRef.current;
    safeSetTimeout(() => {
      if (generationRef.current !== generation) return;
      if (pendingCancelRef.current || isShowingNotification.current) return;
      showNextNotification();
    }, TRANSITION_DELAY_MS);
  }, [notificationsQueue.length, showNextNotification, safeSetTimeout]);

  const previousExternalStateRef = useRef(state);
  useEffect(() => {
    if (previousExternalStateRef.current === state) return;
    previousExternalStateRef.current = state;

    if (state === DreamerState.Normal || state === DreamerState.Collapsed) {
      if (isShowingNotification.current) {
        cancelNotificationCycle();
      } else if (!pendingCancelRef.current) {
        generationRef.current++;
        clearAllTimeouts();
        isTransitioning.current = false;
      }
      setInternalState(state);
    } else if (!isTransitioning.current) {
      transitionToState(state);
    }
  }, [state, cancelNotificationCycle, clearAllTimeouts, transitionToState]);

  useEffect(() => {
    if (rive && rive.activeArtboard) {
      rive.setTextRunValue("Title", title);
    }
  }, [rive, title]);

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <RiveComponent />
    </div>
  );
};

export default DreamerButton;
